import React from 'react';
import { clickableProps, CLICKABLE_CARD } from '../../utils/clickable';
import { cardClass, cardTone } from '../UI/dashboardCard';

/**
 * KpiCard — metric card for dashboard summaries.
 *
 * Props (all existing props preserved for backwards compat):
 *   label    {string}           — Metric name shown above the value
 *   value    {string|number}    — The big number / stat
 *   color    {string}           — a colour from UI/dashboardCard (blue, green,
 *                                 red, amber, gray, purple, …)
 *   icon     {React.ReactNode}  — Optional Lucide icon (18–20 px) — new
 *   subtitle {string}           — Optional small line below value   — new
 *   right    {React.ReactNode}  — Legacy slot, still rendered when no icon
 *   onClick  {function}         — Optional: the card opens what it counts
 *   title    {string}           — Optional tooltip (e.g. where a click goes)
 *   compact  {boolean}          — Smaller value that wraps instead of being
 *                                 clipped, for four-up in a narrow column
 */
const KpiCard = ({ label, value, color = 'blue', icon = null, subtitle = null, right = null, compact = false, onClick = null, title }) => {
  const p = cardTone(color);

  return (
    <div
      {...clickableProps(onClick, title)}
      title={title}
      className={`${cardClass(color)} overflow-hidden flex flex-col ${onClick ? CLICKABLE_CARD : ''}`}
    >
      <div className={`${compact ? 'px-3 pt-3 pb-2' : 'p-4 pt-5'} flex items-start justify-between gap-3 flex-1`}>
        {/* Label + value */}
        <div className="min-w-0">
          {/* Compact labels are never truncated: the point of the card is the
              wording, so the type drops and the letter-spacing goes, and it
              wraps rather than losing the end of a word. */}
          <p className={`font-semibold text-gray-500 dark:text-slate-400 uppercase ${
            compact ? 'text-[9px] tracking-normal break-words' : 'text-[11px] tracking-wide truncate'
          }`}>{label}</p>
          {/* The card hides its overflow, so in compact mode the value is
              smaller and allowed to wrap — a long amount must never be cut. */}
          <p className={`font-bold ${compact ? 'mt-0.5 text-lg leading-tight break-words' : 'mt-1 text-2xl leading-none'} ${p.value}`}>
            {value ?? '—'}
          </p>
          {subtitle && (
            <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400 truncate">{subtitle}</p>
          )}
        </div>

        {/* Icon bubble (new) or legacy right slot */}
        {icon ? (
          <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${p.icon}`}>
            {icon}
          </div>
        ) : right ? (
          <div className="flex-shrink-0">{right}</div>
        ) : null}
      </div>
    </div>
  );
};

export default KpiCard;
